import React, { useState } from 'react'
import {
  Table, Card, Space, Tag, Button, DatePicker, Input, Select, Modal,
  Form, Typography, Popconfirm, message, Tooltip, Alert, theme,
} from 'antd'
import { EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Text, Paragraph } = Typography

interface ActivityLog {
  id: number
  azione: string
  entita: string | null
  entita_id: number | null
  descrizione: string | null
  ip_address: string | null
  user_agent: string | null
  dettagli: Record<string, any> | null
  timestamp: string
  user_id: number | null
  user_nome_completo: string | null
}

const ACTION_COLOR: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  login: 'orange',
  login_success: 'cyan',
  login_failed: 'purple',
  state_change: 'geekblue',
  rda_transition: 'volcano',
  approval: 'lime',
  rejection: 'magenta',
}

const ACTION_LABEL: Record<string, string> = {
  create: 'Creazione',
  update: 'Modifica',
  delete: 'Eliminazione',
  login: 'Login',
  login_success: 'Login',
  login_failed: 'Login fallito',
  state_change: 'Cambio stato',
  rda_transition: 'Transizione RDA',
  approval: 'Approvazione',
  rejection: 'Rifiuto',
}

const AZIONE_OPTIONS = [
  { value: 'create', label: 'Creazione' },
  { value: 'update', label: 'Modifica' },
  { value: 'delete', label: 'Eliminazione' },
  { value: 'login_success', label: 'Login' },
  { value: 'login_failed', label: 'Login fallito' },
  { value: 'state_change', label: 'Cambio stato' },
  { value: 'rda_transition', label: 'Transizione RDA' },
  { value: 'approval', label: 'Approvazione' },
  { value: 'rejection', label: 'Rifiuto' },
]

const ENTITA_OPTIONS = [
  'customer', 'vehicle', 'work_order', 'intervention', 'user',
  'part', 'tire', 'document', 'purchase_request', 'supplier',
  'rda', 'messaggio_interno', 'comunicazione', 'system_table',
  'activity_log', 'auth',
]

// Render strutturato del diff before/after nella riga espansa
const LogDetail: React.FC<{ record: ActivityLog }> = ({ record }) => {
  const { token } = theme.useToken()
  const { dettagli, user_agent, azione } = record

  // Diff array (nuovo formato): [{campo, label, prima, dopo}]
  const diffArray: Array<{ campo: string; label: string; prima: string; dopo: string }> | null =
    Array.isArray(dettagli?.diff) && dettagli.diff.length > 0 ? dettagli.diff : null

  // Diff object (vecchio formato): {campo: {vecchio, nuovo}}
  const diffObj: Record<string, { vecchio: string; nuovo: string }> | null =
    dettagli?.diff && !Array.isArray(dettagli.diff) ? dettagli.diff : null

  // Stato prima/dopo (state_change o rda_transition senza diff array)
  const hasPrimaDopo = dettagli?.prima !== undefined && dettagli?.dopo !== undefined

  // Dettagli extra da mostrare (escludi diff, prima, dopo, status_code, endpoint, http_method)
  const extraKeys = dettagli
    ? Object.keys(dettagli).filter(k => !['diff', 'prima', 'dopo', 'status_code', 'endpoint', 'http_method'].includes(k))
    : []

  return (
    <Space direction="vertical" style={{ width: '100%', padding: '4px 8px' }} size={8}>
      {/* Diff tabella — nuovo formato */}
      {diffArray && (
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              {['Campo', 'Prima', 'Dopo'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '2px 8px', borderBottom: `1px solid ${token.colorBorderSecondary}`, color: token.colorTextTertiary, fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diffArray.map(row => (
              <tr key={row.campo}>
                <td style={{ padding: '2px 8px', fontWeight: 500 }}>{row.label}</td>
                <td style={{ padding: '2px 8px', color: token.colorError, fontFamily: 'monospace' }}>{row.prima || '—'}</td>
                <td style={{ padding: '2px 8px', color: token.colorSuccess, fontFamily: 'monospace' }}>{row.dopo || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Diff object — vecchio formato (retrocompatibilità) */}
      {!diffArray && diffObj && (
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              {['Campo', 'Prima', 'Dopo'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '2px 8px', borderBottom: `1px solid ${token.colorBorderSecondary}`, color: token.colorTextTertiary, fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(diffObj).map(([campo, vals]) => (
              <tr key={campo}>
                <td style={{ padding: '2px 8px', fontWeight: 500 }}>{campo}</td>
                <td style={{ padding: '2px 8px', color: token.colorError, fontFamily: 'monospace' }}>{vals.vecchio || '—'}</td>
                <td style={{ padding: '2px 8px', color: token.colorSuccess, fontFamily: 'monospace' }}>{vals.nuovo || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Prima/dopo per cambio stato senza diff tabella */}
      {!diffArray && !diffObj && hasPrimaDopo && (
        <Text style={{ fontSize: 12 }}>
          Stato: <span style={{ color: token.colorError, fontFamily: 'monospace' }}>{dettagli.prima || '—'}</span>
          {' → '}
          <span style={{ color: token.colorSuccess, fontFamily: 'monospace' }}>{dettagli.dopo || '—'}</span>
          {dettagli.motivo && <span style={{ color: token.colorTextTertiary }}> — {dettagli.motivo}</span>}
        </Text>
      )}

      {/* Login failed: email tentata + motivo */}
      {azione === 'login_failed' && dettagli?.email_tentata && (
        <Text style={{ fontSize: 12 }}>
          Email: <span style={{ fontFamily: 'monospace' }}>{dettagli.email_tentata}</span>
          {dettagli.motivo && <span style={{ color: token.colorTextTertiary }}> — {dettagli.motivo}</span>}
        </Text>
      )}

      {/* Extra key-value */}
      {extraKeys.length > 0 && (
        <Space wrap size={[16, 4]}>
          {extraKeys.map(k => (
            <Text key={k} type="secondary" style={{ fontSize: 11 }}>
              <b>{k}:</b> {String(dettagli![k])}
            </Text>
          ))}
        </Space>
      )}

      {/* User agent */}
      {user_agent && (
        <Text type="secondary" style={{ fontSize: 11 }}>UA: {user_agent}</Text>
      )}
    </Space>
  )
}

const fetchLogs = async (params: Record<string, any>) => {
  const res = await axiosInstance.get('/activity-logs/', { params })
  return res.data as { total: number; items: ActivityLog[] }
}

const ActivityLogPanel: React.FC = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [editingLog, setEditingLog] = useState<ActivityLog | null>(null)
  const [editForm] = Form.useForm()
  const [filterForm] = Form.useForm()
  const [bulkForm] = Form.useForm()
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkPreviewCount, setBulkPreviewCount] = useState<number | null>(null)
  const [bulkPreviewing, setBulkPreviewing] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['activity-logs', page, pageSize, filters],
    queryFn: () => fetchLogs({ skip: (page - 1) * pageSize, limit: pageSize, ...filters }),
    retry: 1,
    refetchInterval: 15000,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      axiosInstance.put(`/activity-logs/${id}`, payload),
    onSuccess: () => {
      message.success('Log aggiornato')
      queryClient.invalidateQueries({ queryKey: ['activity-logs'] })
      setEditingLog(null)
      editForm.resetFields()
    },
    onError: () => message.error('Errore aggiornamento'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => axiosInstance.delete(`/activity-logs/${id}`),
    onSuccess: () => {
      message.success('Log eliminato')
      queryClient.invalidateQueries({ queryKey: ['activity-logs'] })
    },
    onError: () => message.error('Errore eliminazione'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: (params: Record<string, any>) =>
      axiosInstance.delete('/activity-logs/bulk', { params }),
    onSuccess: (res) => {
      message.success(`Eliminati ${res.data.deleted} log`)
      queryClient.invalidateQueries({ queryKey: ['activity-logs'] })
      closeBulkModal()
    },
    onError: () => message.error('Errore nella cancellazione massiva'),
  })

  const closeBulkModal = () => {
    setBulkModalOpen(false)
    setBulkPreviewCount(null)
    bulkForm.resetFields()
  }

  const buildBulkParams = (vals: any): Record<string, any> => {
    const params: Record<string, any> = {}
    if (vals.dateRange?.length === 2) {
      params.start_date = vals.dateRange[0].format('YYYY-MM-DD')
      params.end_date = vals.dateRange[1].format('YYYY-MM-DD')
    }
    if (vals.azione) params.azione = vals.azione
    if (vals.entita) params.entita = vals.entita
    if (vals.user_name) params.user_name = vals.user_name
    return params
  }

  const handlePreviewBulk = async () => {
    try {
      const vals = await bulkForm.validateFields()
      const params = buildBulkParams(vals)
      setBulkPreviewing(true)
      const res = await axiosInstance.get('/activity-logs/', { params: { ...params, skip: 0, limit: 1 } })
      setBulkPreviewCount(res.data.total)
    } catch {
      // form validation failed
    } finally {
      setBulkPreviewing(false)
    }
  }

  const handleBulkDelete = async () => {
    try {
      const vals = await bulkForm.validateFields()
      const params = buildBulkParams(vals)
      bulkDeleteMutation.mutate(params)
    } catch {
      // form validation failed
    }
  }

  const columns: ColumnsType<ActivityLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 150,
      render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm:ss'),
    },
    {
      title: 'Azione',
      dataIndex: 'azione',
      width: 120,
      render: (v: string) => <Tag color={ACTION_COLOR[v] ?? 'default'}>{ACTION_LABEL[v] ?? v}</Tag>,
    },
    {
      title: 'Entità',
      dataIndex: 'entita',
      width: 130,
      render: (v: string | null, r: ActivityLog) =>
        v ? `${v}${r.entita_id ? ` #${r.entita_id}` : ''}` : '—',
    },
    {
      title: 'Utente',
      dataIndex: 'user_nome_completo',
      width: 140,
      render: (v: string | null, r: ActivityLog) => v ?? (r.user_id ? `#${r.user_id}` : '—'),
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Descrizione',
      dataIndex: 'descrizione',
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: ActivityLog) => (
        <Space size={4}>
          <Tooltip title="Modifica">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingLog(record)
                editForm.setFieldsValue({
                  descrizione: record.descrizione,
                  dettagli: record.dettagli ? JSON.stringify(record.dettagli, null, 2) : '',
                })
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Eliminare questo log?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Elimina"
            cancelText="Annulla"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleSearch = (vals: any) => {
    const params: Record<string, any> = {}
    if (vals.azione) params.azione = vals.azione
    if (vals.entita) params.entita = vals.entita
    if (vals.cerca_nominativo) params.user_name = vals.cerca_nominativo
    if (vals.cerca_descrizione) params.cerca_descrizione = vals.cerca_descrizione
    if (vals.ip_address) params.ip_address = vals.ip_address
    if (vals.dateRange?.length === 2) {
      params.start_date = vals.dateRange[0].format('YYYY-MM-DD')
      params.end_date = vals.dateRange[1].format('YYYY-MM-DD')
    }
    setFilters(params)
    setPage(1)
  }

  const handleResetFilters = () => {
    filterForm.resetFields()
    setFilters({})
    setPage(1)
  }

  const handleEditSubmit = (vals: any) => {
    if (!editingLog) return
    let dettagli: Record<string, any> | undefined
    if (vals.dettagli?.trim()) {
      try {
        dettagli = JSON.parse(vals.dettagli)
      } catch {
        message.error('Dettagli JSON non valido')
        return
      }
    }
    updateMutation.mutate({
      id: editingLog.id,
      payload: { descrizione: vals.descrizione || null, dettagli },
    })
  }

  return (
    <>
      <Card
        title="Log Attività"
        size="small"
        extra={
          <Button danger size="small" onClick={() => setBulkModalOpen(true)}>
            Cancellazione massiva…
          </Button>
        }
      >
        <Form
          form={filterForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}
        >
          <Form.Item name="azione">
            <Select placeholder="Azione" style={{ width: 160 }} allowClear>
              {AZIONE_OPTIONS.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="entita">
            <Select placeholder="Entità" style={{ width: 150 }} allowClear>
              {ENTITA_OPTIONS.map(e => (
                <Select.Option key={e} value={e}>{e}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="cerca_nominativo">
            <Input placeholder="Nominativo utente" style={{ width: 160 }} allowClear />
          </Form.Item>
          <Form.Item name="cerca_descrizione">
            <Input placeholder="Cerca in descrizione" style={{ width: 180 }} allowClear />
          </Form.Item>
          <Form.Item name="ip_address">
            <Input placeholder="IP address" style={{ width: 130 }} allowClear />
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker format="DD/MM/YYYY" style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Filtra</Button>
              <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>

        {isError && (
          <Alert
            type="error"
            message="Errore nel caricamento dei log"
            description={
              (error as any)?.response?.data?.detail
                ? JSON.stringify((error as any).response.data.detail)
                : (error as any)?.message ?? 'Errore sconosciuto'
            }
            showIcon
            closable
            style={{ marginBottom: 12 }}
          />
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            onChange: setPage,
            showTotal: (t) => `${t} record`,
          }}
          expandable={{
            expandedRowRender: (record) => <LogDetail record={record} />,
            rowExpandable: (r) => !!(r.dettagli || r.user_agent),
          }}
        />
      </Card>

      {/* Edit modal */}
      <Modal
        title="Modifica log"
        open={!!editingLog}
        onCancel={() => { setEditingLog(null); editForm.resetFields() }}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isPending}
        okText="Salva"
        cancelText="Annulla"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item name="descrizione" label="Descrizione">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="dettagli" label="Dettagli (JSON)">
            <Input.TextArea rows={6} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk delete modal */}
      <Modal
        title="Cancellazione massiva log"
        open={bulkModalOpen}
        onCancel={closeBulkModal}
        footer={null}
        width={520}
      >
        <Form form={bulkForm} layout="vertical">
          <Form.Item
            name="dateRange"
            label="Periodo (obbligatorio)"
            rules={[{ required: true, message: 'Seleziona il periodo da eliminare' }]}
          >
            <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="azione" label="Azione (opzionale)">
            <Select placeholder="Tutte le azioni" allowClear>
              {AZIONE_OPTIONS.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="entita" label="Entità (opzionale)">
            <Select placeholder="Tutte le entità" allowClear>
              {ENTITA_OPTIONS.map(e => (
                <Select.Option key={e} value={e}>{e}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="user_name" label="Nominativo utente (opzionale)">
            <Input placeholder="Nome o cognome — lascia vuoto per tutti gli utenti" allowClear />
          </Form.Item>

          {bulkPreviewCount !== null && (
            <Alert
              type={bulkPreviewCount === 0 ? 'info' : 'warning'}
              message={
                bulkPreviewCount === 0
                  ? 'Nessun record trovato con questi criteri'
                  : `Verranno eliminati ${bulkPreviewCount} record. Questa operazione è irreversibile.`
              }
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={closeBulkModal}>Annulla</Button>
            <Button loading={bulkPreviewing} onClick={handlePreviewBulk}>
              Verifica
            </Button>
            <Button
              danger
              loading={bulkDeleteMutation.isPending}
              disabled={bulkPreviewCount === null || bulkPreviewCount === 0}
              onClick={handleBulkDelete}
            >
              {bulkPreviewCount != null && bulkPreviewCount > 0
                ? `Elimina ${bulkPreviewCount} record`
                : 'Elimina'}
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  )
}

export default ActivityLogPanel
